import React, { useState, useEffect, useCallback } from 'react';
import { invoiceAPI, bookingAPI } from '../services/api';
import toast from 'react-hot-toast';

const STATUS_BADGE = {
  draft: 'badge-gray', sent: 'badge-blue',
  paid: 'badge-green', overdue: 'badge-red', cancelled: 'badge-amber',
};

const emptyForm = {
  booking: '', dueDate: '', notes: '',
  items: [{ description: '', quantity: 1, unitPrice: '', total: 0 }],
};

export default function InvoicePage() {
  const [invoices, setInvoices] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [viewInvoice, setViewInvoice] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [iRes, bRes] = await Promise.all([
        invoiceAPI.getAll(),
        bookingAPI.getAll({ status: 'completed', limit: 100 }),
      ]);
      setInvoices(iRes.data.data || []);
      setBookings(bRes.data.data || []);
    } catch { toast.error('Failed to load invoices'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const setField = (f) => (e) => setForm(p => ({ ...p, [f]: e.target.value }));

  const setItem = (idx, field, val) => {
    setForm(p => {
      const items = [...p.items];
      items[idx] = { ...items[idx], [field]: val };
      if (field === 'quantity' || field === 'unitPrice') {
        items[idx].total = (parseFloat(items[idx].quantity) || 0) * (parseFloat(items[idx].unitPrice) || 0);
      }
      return { ...p, items };
    });
  };

  const addItem = () => setForm(p => ({ ...p, items: [...p.items, { description: '', quantity: 1, unitPrice: '', total: 0 }] }));
  const removeItem = (idx) => setForm(p => ({ ...p, items: p.items.filter((_, i) => i !== idx) }));

  const subtotal = form.items.reduce((s, i) => s + (i.total || 0), 0);
  const tax = subtotal * 0.18;
  const total = subtotal + tax;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.booking) { toast.error('Select a booking'); return; }
    setSaving(true);
    try {
      await invoiceAPI.create({ ...form, subtotal, tax, total });
      toast.success('Invoice created');
      setShowModal(false);
      setForm(emptyForm);
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to create invoice');
    } finally { setSaving(false); }
  };

  const updateStatus = async (id, status) => {
    try {
      await invoiceAPI.updateStatus(id, status);
      toast.success(`Invoice marked as ${status}`);
      load();
    } catch { toast.error('Update failed'); }
  };

  const fmt = (n) => `₹${Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}`;
  const fmtDate = (d) => new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });

  const filtered = invoices.filter(inv => {
    const q = search.toLowerCase();
    const matchSearch = !q || inv.invoiceNumber?.toLowerCase().includes(q) || inv.customer?.name?.toLowerCase().includes(q);
    const matchStatus = statusFilter === 'all' || inv.status === statusFilter;
    return matchSearch && matchStatus;
  });

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <div>
          <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 20, fontWeight: 700 }}>Invoices</h2>
          <p style={{ color: 'var(--text-muted)', fontSize: 13, marginTop: 2 }}>{filtered.length} invoices</p>
        </div>
        <button className="btn btn-primary" onClick={() => { setForm(emptyForm); setShowModal(true); }}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
          New Invoice
        </button>
      </div>

      <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
        <div className="search-bar" style={{ flex: 1, minWidth: 200 }}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
          <input placeholder="Search invoices…" value={search} onChange={e => setSearch(e.target.value)} />
        </div>
        <div className="tabs" style={{ margin: 0 }}>
          {['all','draft','sent','paid','overdue','cancelled'].map(s => (
            <button key={s} className={`tab-btn ${statusFilter === s ? 'active' : ''}`}
              onClick={() => setStatusFilter(s)}
              style={{ textTransform: 'capitalize', padding: '7px 12px', fontSize: 12 }}>
              {s === 'all' ? 'All' : s}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="loading-screen" style={{ height: 300 }}><div className="spinner" /></div>
      ) : filtered.length === 0 ? (
        <div className="card"><div className="empty-state">
          <div className="empty-state-icon">🧾</div>
          <div className="empty-state-title">No invoices found</div>
          <div className="empty-state-text">Generate invoices from completed bookings.</div>
        </div></div>
      ) : (
        <div className="table-wrapper">
          <table>
            <thead><tr>
              <th>Invoice #</th><th>Customer</th><th>Booking</th>
              <th>Amount</th><th>Due Date</th><th>Status</th><th>Actions</th>
            </tr></thead>
            <tbody>
              {filtered.map(inv => (
                <tr key={inv._id}>
                  <td style={{ fontWeight: 600, color: 'var(--accent)' }}>{inv.invoiceNumber}</td>
                  <td>{inv.customer?.name || '—'}</td>
                  <td style={{ color: 'var(--text-muted)', fontSize: 13 }}>{inv.booking?.bookingNumber || '—'}</td>
                  <td style={{ fontWeight: 600 }}>{fmt(inv.total)}</td>
                  <td>{inv.dueDate ? fmtDate(inv.dueDate) : '—'}</td>
                  <td><span className={`badge ${STATUS_BADGE[inv.status] || 'badge-gray'}`}>{inv.status}</span></td>
                  <td>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button className="btn btn-ghost btn-sm" onClick={() => setViewInvoice(inv)}>View</button>
                      {inv.status === 'draft' && (
                        <button className="btn btn-ghost btn-sm" onClick={() => updateStatus(inv._id, 'sent')}>Send</button>
                      )}
                      {inv.status === 'sent' && (
                        <button className="btn btn-ghost btn-sm" style={{ color: 'var(--success)' }} onClick={() => updateStatus(inv._id, 'paid')}>Mark Paid</button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Create Invoice Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setShowModal(false)}>
          <div className="modal" style={{ maxWidth: 680 }}>
            <div className="modal-header">
              <span className="modal-title">New Invoice</span>
              <button className="modal-close" onClick={() => setShowModal(false)}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="20" height="20"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
              </button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="modal-body">
                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">Booking (Completed) *</label>
                    <select className="form-select" value={form.booking} onChange={setField('booking')} required>
                      <option value="">Select booking</option>
                      {bookings.map(b => (
                        <option key={b._id} value={b._id}>{b.bookingNumber} — {b.customer?.name}</option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Due Date</label>
                    <input type="date" className="form-input" value={form.dueDate} onChange={setField('dueDate')} />
                  </div>
                </div>

                {/* Line Items */}
                <div style={{ marginBottom: 16 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                    <label className="form-label" style={{ margin: 0 }}>Line Items</label>
                    <button type="button" className="btn btn-ghost btn-sm" onClick={addItem}>+ Add Row</button>
                  </div>
                  <div style={{ border: '1px solid var(--border)', borderRadius: 8, overflow: 'hidden' }}>
                    <table style={{ margin: 0 }}>
                      <thead><tr>
                        <th style={{ width: '45%' }}>Description</th>
                        <th style={{ width: '15%' }}>Qty</th>
                        <th style={{ width: '20%' }}>Unit Price</th>
                        <th style={{ width: '15%' }}>Total</th>
                        <th style={{ width: '5%' }}></th>
                      </tr></thead>
                      <tbody>
                        {form.items.map((item, idx) => (
                          <tr key={idx}>
                            <td><input className="form-input" style={{ margin: 0 }} placeholder="Service / Part" value={item.description} onChange={e => setItem(idx, 'description', e.target.value)} /></td>
                            <td><input type="number" className="form-input" style={{ margin: 0 }} min="1" value={item.quantity} onChange={e => setItem(idx, 'quantity', e.target.value)} /></td>
                            <td><input type="number" className="form-input" style={{ margin: 0 }} placeholder="0" value={item.unitPrice} onChange={e => setItem(idx, 'unitPrice', e.target.value)} /></td>
                            <td style={{ fontWeight: 600, color: 'var(--text-primary)' }}>{fmt(item.total)}</td>
                            <td><button type="button" style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--danger)', fontSize: 16 }} onClick={() => removeItem(idx)}>×</button></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Totals */}
                <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <div style={{ width: 260 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid var(--border)' }}>
                      <span style={{ color: 'var(--text-muted)' }}>Subtotal</span>
                      <span>{fmt(subtotal)}</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid var(--border)' }}>
                      <span style={{ color: 'var(--text-muted)' }}>GST (18%)</span>
                      <span>{fmt(tax)}</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', fontWeight: 700, fontSize: 16 }}>
                      <span>Total</span>
                      <span style={{ color: 'var(--accent)' }}>{fmt(total)}</span>
                    </div>
                  </div>
                </div>

                <div className="form-group">
                  <label className="form-label">Notes</label>
                  <textarea className="form-textarea" placeholder="Payment terms, thank you note…" value={form.notes} onChange={setField('notes')} />
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-ghost" onClick={() => setShowModal(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={saving}>{saving ? 'Saving…' : 'Create Invoice'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* View Invoice Modal */}
      {viewInvoice && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setViewInvoice(null)}>
          <div className="modal" style={{ maxWidth: 600 }}>
            <div className="modal-header">
              <span className="modal-title">Invoice {viewInvoice.invoiceNumber}</span>
              <button className="modal-close" onClick={() => setViewInvoice(null)}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="20" height="20"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
              </button>
            </div>
            <div className="modal-body">
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 20 }}>
                <div>
                  <div style={{ fontWeight: 700, fontSize: 18, fontFamily: 'var(--font-display)' }}>🔧 GarageOS</div>
                  <div style={{ color: 'var(--text-muted)', fontSize: 13 }}>Professional Auto Services</div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <span className={`badge ${STATUS_BADGE[viewInvoice.status]}`} style={{ fontSize: 13 }}>{viewInvoice.status}</span>
                  {viewInvoice.dueDate && <div style={{ color: 'var(--text-muted)', fontSize: 12, marginTop: 4 }}>Due: {fmtDate(viewInvoice.dueDate)}</div>}
                </div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 20, padding: 16, background: 'var(--bg-base)', borderRadius: 8 }}>
                <div>
                  <div style={{ fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 1 }}>Bill To</div>
                  <div style={{ fontWeight: 600, marginTop: 4 }}>{viewInvoice.customer?.name}</div>
                  <div style={{ color: 'var(--text-muted)', fontSize: 13 }}>{viewInvoice.customer?.email}</div>
                </div>
                <div>
                  <div style={{ fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 1 }}>Booking</div>
                  <div style={{ fontWeight: 600, marginTop: 4 }}>{viewInvoice.booking?.bookingNumber}</div>
                  <div style={{ color: 'var(--text-muted)', fontSize: 13, textTransform: 'capitalize' }}>{viewInvoice.booking?.serviceType?.replace(/-/g, ' ')}</div>
                </div>
              </div>
              <div className="table-wrapper" style={{ marginBottom: 16 }}>
                <table>
                  <thead><tr><th>Description</th><th>Qty</th><th>Unit Price</th><th>Total</th></tr></thead>
                  <tbody>
                    {(viewInvoice.items || []).map((item, i) => (
                      <tr key={i}>
                        <td>{item.description}</td>
                        <td>{item.quantity}</td>
                        <td>{fmt(item.unitPrice)}</td>
                        <td style={{ fontWeight: 600 }}>{fmt(item.total)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                <div style={{ width: 240 }}>
                  {[['Subtotal', viewInvoice.subtotal], ['GST (18%)', viewInvoice.tax]].map(([l, v]) => (
                    <div key={l} style={{ display: 'flex', justifyContent: 'space-between', padding: '5px 0', borderBottom: '1px solid var(--border)', color: 'var(--text-muted)', fontSize: 13 }}>
                      <span>{l}</span><span>{fmt(v)}</span>
                    </div>
                  ))}
                  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', fontWeight: 700, fontSize: 16 }}>
                    <span>Total</span><span style={{ color: 'var(--accent)' }}>{fmt(viewInvoice.total)}</span>
                  </div>
                </div>
              </div>
              {viewInvoice.notes && <p style={{ color: 'var(--text-muted)', fontSize: 13, marginTop: 12, fontStyle: 'italic' }}>{viewInvoice.notes}</p>}
            </div>
            <div className="modal-footer">
              <button className="btn btn-ghost" onClick={() => setViewInvoice(null)}>Close</button>
              {viewInvoice.status === 'sent' && (
                <button className="btn btn-primary" onClick={() => { updateStatus(viewInvoice._id, 'paid'); setViewInvoice(null); }}>Mark as Paid</button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}